import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ShieldCheck, Loader2 } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[44%] bg-[#0B2545] text-white p-12">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-sm bg-[#00A9E0] flex items-center justify-center font-bold text-lg">C</div>
            <div className="cs-heading text-xl font-semibold tracking-tight">CloudScore<span className="text-[#00A9E0]">.ai</span></div>
          </div>
          <h1 className="cs-heading text-3xl font-semibold mt-16 leading-tight max-w-md">
            The agentic FinOps platform for GCP — and the MSPs who bill on it.
          </h1>
          <p className="text-sm text-white/70 mt-4 max-w-md leading-relaxed">
            DeepWaste detection, closed-loop savings ledger, and PartnerOps billing-grade
            invoicing on a FOCUS-normalized ledger.
          </p>
        </div>
        <div className="text-[11px] text-white/50">© 2026 CloudScore Inc. · SOC 2 Type II controls · FOCUS 1.3/1.4 aligned</div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center bg-[hsl(var(--cs-tint))] px-6">
        <form onSubmit={submit} className="w-full max-w-sm cs-card p-8" data-testid="login-form">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-[#00579e]" />
            <h2 className="cs-heading text-lg font-semibold">Sign in</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-6">Use your CloudScore workspace credentials.</p>

          <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Email</label>
          <Input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com" autoComplete="email" required
            data-testid="login-email-input" className="rounded-sm mt-1 mb-4"
          />
          <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Password</label>
          <Input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" autoComplete="current-password" required
            data-testid="login-password-input" className="rounded-sm mt-1"
          />

          {error && (
            <div className="text-xs text-[#b91c1c] bg-[#fee2e2] border border-[#ef4444]/40 rounded-sm p-2 mt-4" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit" disabled={busy} data-testid="login-submit-button"
            className="w-full mt-6 rounded-sm bg-[#00579e] hover:bg-[#004580] text-white h-10"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
